import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Table, PageSizeType, Sort } from 'design-system-web';
import { SortDirectionEnum } from 'constants-activate';
import { GroupType, SortDirection } from 'graphql-query-contracts';
import { useChannelAdminContext } from 'hooks';
import { useCostOffsetCodes } from 'react-hooks';

import { useGetCostOffsetCodesTableColumns } from '../helpers/useGetCostOffsetCodesTableColumns';

const DEFAULT_SORT: Sort = {
  id: 'code',
  direction: SortDirectionEnum.Asc,
};

const DEFAULT_PAGE_SIZE: PageSizeType = 50;

export function CostOffsetCodeTable() {
  const { channel } = useChannelAdminContext();
  const groupId = channel?._id;

  const [pageSize, setPageSize] = useState<PageSizeType>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);

  const { costOffsetCodes, pageInfo, fetchCostOffsetCodes, loading } =
    useCostOffsetCodes();

  const fetchVariables = useMemo(
    () => ({
      groupId: groupId ?? '',
      groupType: GroupType.GroupTypeActivateChannel,
      pagination: {
        perPage: pageSize,
        start: page * pageSize,
      },
      sortBy:
        sort.id === 'code'
          ? {
              key: 'code' as const,
              direction:
                sort.direction === SortDirectionEnum.Desc
                  ? SortDirection.Desc
                  : SortDirection.Asc,
            }
          : undefined,
    }),
    [groupId, page, pageSize, sort]
  );

  useEffect(() => {
    if (!groupId) return;

    fetchCostOffsetCodes(fetchVariables);
  }, [fetchCostOffsetCodes, fetchVariables, groupId]);

  const refetch = useCallback(async () => {
    await fetchCostOffsetCodes(fetchVariables);
  }, [fetchCostOffsetCodes, fetchVariables]);

  const columns = useGetCostOffsetCodesTableColumns({ refetch });

  return (
    <Table
      columns={columns}
      data={costOffsetCodes}
      isLoading={loading}
      totalRows={pageInfo?.total}
      pageSize={pageSize}
      pagination="server"
      page={page}
      onPageChange={setPage}
      onPageSizeChange={nextSize => {
        setPageSize(nextSize);
        setPage(0);
      }}
      onSortChange={nextSort => {
        setSort(nextSort);
        setPage(0);
      }}
    />
  );
}
